import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography, Box } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useTranslations } from 'hooks/useTranslations';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { Chips } from 'components/atoms/Chips';
import { EApplicationProcessStatus, statusApplicationProcessChipVariant } from '../helper';
import { EPermission } from 'models/permissions';

interface ILicencePricesTableCard {
  data: ITableDataSource;
  handleView?: (arg: string) => void;
  handleDelete?: (arg: string) => void;
}

const LicenceApplicationProcessTableCard = ({
  data,
  handleView,
  handleDelete,
}: ILicencePricesTableCard) => {
  const { t } = useTranslations();

  return (
    <Grid
      container
      size={{ xs: 12 }}
      spacing={2}
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        mb: 2,
        p: 2,
        backgroundColor: colors.white,
      }}
    >
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licence.processType.type.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.type)}</Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licence.processType.legalEntityType.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.legalEntityType)}</Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licence.processType.status.column')}
        </Typography>
        <Box>
          <Chips
            text={data.status}
            variant={statusApplicationProcessChipVariant(data.statusUniqueId)}
            fullWidth
          />
        </Box>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licence.licencePricesTable.price.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.fee)}</Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licence.licencePricesTable.priceName.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.name)}</Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licence.licencePricesTable.priceDescription.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.description)}</Typography>
      </Grid>

      <Grid size={{ xs: 12 }} sx={{ mt: 1, pt: 2, borderTop: `1px solid ${colors.gray200}` }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: colors.gray500, textTransform: 'uppercase' }}
          >
            {t('licence.licencePricesTable.action.column')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: '150px' } }}>
              <Button
                variant={EButtonVariants.outlined}
                label={t('licence.licencePricesTable.details.link')}
                onClick={() => handleView && handleView(`${data.id}`)}
                size={EButtonSizes.small}
                fullWidth
              />
            </Box>
            {data.statusUniqueId === EApplicationProcessStatus.draft && (
              <Box sx={{ width: { xs: '100%', sm: '150px' } }}>
                <Button
                  variant={EButtonVariants.outlined}
                  label={t('licence.licencePricesTable.delete.link')}
                  onClick={() => handleDelete && handleDelete(`${data.id}`)}
                  size={EButtonSizes.small}
                  fullWidth
                  permission={EPermission.MANAGE_WEAPON_LICENCE_CONFIGURATOR}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export { LicenceApplicationProcessTableCard };
